import React from 'react';
import styled from 'styled-components';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';
import colors from 'common/styles/colors';
import Label from 'common/typography/Label/Label';
import Text_T from 'common/typography/Text/Text';
import LoadingSvg from 'common/images/LoadingSvg';
import { humanize } from 'common/helpers';
import DeleteIconSvg from 'common/images/BearingModal/DeleteIconSvg';
import { FlexContainer } from 'common/components/atoms';
import { deleteNote } from 'home/Activity/actions/activity.actions';
import activityFormat from 'home/Activity/components/ActivityFormat';
import MetadataContainer from 'common/components/atoms/MetadataContainer';
import SecondaryText_T from 'common/typography/SecondaryText/SecondaryText';
import { formatDate } from 'common/utils';
import { formatCurrency } from 'common/utils/currencyUtils';
import { Paperclip } from 'common/images/FaIcons';
import { Trans, useTranslation } from 'react-i18next';
import { deleteRepair } from '../actions/repairs.actions';
import Attachments from '../organisms/Attachments';
import DiffViewer from './atoms/DiffViewer';

const LabelContainer = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${colors.greyD};
  margin-bottom: 1rem;
  ${Label} {
    margin: 0;
  }
`;

const NoDataContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 4rem;
  color: ${colors.greyD};
`;

const TaskItemContainer = styled.div`
  display: flex;
  padding: 1rem;
  position: relative;
  margin-bottom: 1rem;
  background-color: ${colors.greyXXXL};
  border-radius: 5px;
  border: 1px solid ${colors.white};
  ${props => props.clickable && `
    cursor: pointer;
    &:hover {
      background-color: ${colors.greyXXL};
      border: 1px solid ${colors.greyD};
    }
  `};
`;

const Text = styled(Text_T)`
  font-size: 14px;
  word-break: break-word;
  ${props => props.color && `color: ${props.color};`}
`;

const SecondaryText = styled(SecondaryText_T)`
  font-size: 12px;
  line-height: 160%;
  ${props => props.marginTop && `margin-top: ${props.marginTop};`}
`;

const IconContainer = styled(FlexContainer).attrs({
  justifyContent: 'center',
  alignItems: 'center'
})`
  ${props => props.paddingRight && `padding-right: ${props.paddingRight};`}
`;

const formHistorySentence = (item, getItemField, t, currency = 'USD') => {
  const renderTransSentence = (i18nKey, defaults, values) => (
    <Trans
      i18nKey={i18nKey}
      defaults={defaults}
      values={values}
      components={{ b: <b /> }}
    />
  );
  if (item.resource_type === 'task') {
    if (item.op === 'ADD') {
      return renderTransSentence(
        'taskHistory.sentences.taskCreated',
        '<b>{{userName}}</b> created this task in <b>{{state}}</b>',
        { userName: item.user_name, state: humanize(item.state) }
      );
    }
    switch (item.column) {
      case 'name':
        return renderTransSentence(
          'taskHistory.sentences.taskRenamed',
          '<b>{{userName}}</b> renamed this task to <b>{{name}}</b>',
          { userName: item.user_name, name: item.new_value }
        );
      case 'state':
        return renderTransSentence(
          'taskHistory.sentences.taskStateMoved',
          '<b>{{userName}}</b> moved this task from <b>{{from}}</b> to <b>{{to}}</b>',
          { userName: item.user_name, from: humanize(item.old_value), to: humanize(item.new_value) }
        );
      case 'description':
        return renderTransSentence(
          'taskHistory.sentences.taskDescriptionUpdated',
          '<b>{{userName}}</b> updated the task description',
          { userName: item.user_name }
        );
      case 'recommended_action':
        return renderTransSentence(
          'taskHistory.sentences.taskRecommendedActionUpdated',
          '<b>{{userName}}</b> updated the recommended action',
          { userName: item.user_name }
        );
      case 'task_type':
        return renderTransSentence(
          'taskHistory.sentences.taskTypeUpdated',
          '<b>{{userName}}</b> changed task type to <b>{{taskType}}</b>',
          { userName: item.user_name, taskType: humanize(item.new_value) }
        );
      case 'condition':
        return renderTransSentence(
          'taskHistory.sentences.assetConditionChanged',
          '<b>{{userName}}</b> changed the asset condition from <b>{{from}}</b> to <b>{{to}}</b>',
          { userName: item.user_name, from: humanize(item.old_value), to: humanize(item.new_value) }
        );
      case 'defect':
        return (_.isEmpty(item.new_value) ?
          renderTransSentence(
            'taskHistory.sentences.defectRemoved',
            '<b>{{userName}}</b> removed suspected defect <b>{{defect}}</b>',
            { userName: item.user_name, defect: humanize(item.old_value) }
          ) :
          renderTransSentence(
            'taskHistory.sentences.suspectedDefectUpdated',
            '<b>{{userName}}</b> updated suspected defect to <b>{{defect}}</b>',
            { userName: item.user_name, defect: humanize(item.new_value) }
          )
        );
      case 'devices_list': {
        const serials = item.new_value && Array.isArray(item.new_value)
          ? item.new_value.map(it => it.serial_number).join(', ')
          : '';
        return (_.isEmpty(item.new_value) ?
          renderTransSentence(
            'taskHistory.sentences.devicesRemoved',
            '<b>{{userName}}</b> removed selected devices',
            { userName: item.user_name }
          ) :
          renderTransSentence(
            'taskHistory.sentences.devicesSelected',
            '<b>{{userName}}</b> selected devices: <b>{{serials}}</b>',
            { userName: item.user_name, serials }
          )
        );
      }
      case 'user_role_assigned':
        return renderTransSentence(
          'taskHistory.sentences.taskAssigned',
          '<b>{{userName}}</b> assigned this task to <b>{{role}}</b>',
          { userName: item.user_name, role: humanize(item.new_value) }
        );
      case 'customer_savings': {
        const oldVal = item.old_value;
        const newVal = item.new_value;
        const hasOld = oldVal != null && oldVal !== '';
        const hasNew = newVal != null && newVal !== '';
        const oldStr = hasOld ? formatCurrency(Number(oldVal), currency, { decimals: 2 }) : null;
        const newStr = hasNew ? formatCurrency(Number(newVal), currency, { decimals: 2 }) : null;
        if (newStr && !oldStr) {
          return renderTransSentence(
            'taskHistory.sentences.costSavedSet',
            '<b>{{userName}}</b> set the cost saved to <b>{{amount}}</b>',
            { userName: item.user_name, amount: newStr }
          );
        }
        if (newStr && oldStr) {
          return renderTransSentence(
            'taskHistory.sentences.costSavedUpdated',
            '<b>{{userName}}</b> updated the cost saved from <b>{{from}}</b> to <b>{{to}}</b>',
            { userName: item.user_name, from: oldStr, to: newStr }
          );
        }
        if (oldStr && !newStr) {
          return renderTransSentence(
            'taskHistory.sentences.costSavedRemoved',
            '<b>{{userName}}</b> removed the cost saved (was <b>{{amount}}</b>)',
            { userName: item.user_name, amount: oldStr }
          );
        }
        return renderTransSentence(
          'taskHistory.sentences.costSavedChanged',
          '<b>{{userName}}</b> updated the cost saved',
          { userName: item.user_name }
        );
      }
      default:
        return '';
    }
  } else if (item.resource_type === 'attachment') {
    return renderTransSentence(
      'taskHistory.sentences.attachmentsAdded',
      '<b>{{userName}}</b> added <b>{{count}}</b> attachment(s)',
      { userName: item.user_name, count: item.attachments.length }
    );
  } else {
    if (item.op === 'ADD') {
      switch (item.resource_type) {
        case 'note':
          return renderTransSentence(
            'taskHistory.sentences.noteAdded',
            '<b>{{userName}}</b> added a note',
            { userName: item.user_name }
          );
        case 'repair':
          return renderTransSentence(
            'taskHistory.sentences.repairAdded',
            '<b>{{userName}}</b> added a repair: <b>{{name}}</b>',
            { userName: item.user_name, name: getItemField(item, 'name') }
          );
        default:
          return '';
      }
    }
    if (item.op === 'UPDATE') {
      switch (item.resource_type) {
        case 'note':
          return renderTransSentence(
            'taskHistory.sentences.noteUpdated',
            '<b>{{userName}}</b> updated a note',
            { userName: item.user_name }
          );
        case 'repair':
          return renderTransSentence(
            'taskHistory.sentences.repairUpdated',
            '<b>{{userName}}</b> updated repair <b>{{name}}</b>',
            { userName: item.user_name, name: item.name }
          );
        default:
          return '';
      }
    }
    switch (item.resource_type) {
      case 'note':
        return renderTransSentence(
          'taskHistory.sentences.itemDeleted',
          '<b>{{userName}}</b> deleted this <b>{{itemType}}</b>',
          { userName: item.user_name, itemType: humanize(item.resource_type) }
        );
      case 'repair':
        return renderTransSentence(
          'taskHistory.sentences.repairDeleted',
          '<b>{{userName}}</b> deleted repair <b>{{name}}</b>',
          { userName: item.user_name, name: item.name }
        );
      default:
        return '';
    }
  }
};

function TaskHistory({
  items,
  loading,
  setRepairModalOpen,
  setNoteModalOpen,
  deleteNote,
  deleteRepair,
  taskId,
  repairs,
  notes,
  currency
}) {
  const { t } = useTranslation();
  const onItemClickHandler = (item) => {
    if (item.resource_type === 'repair') {
      setRepairModalOpen(item.resource_id);
    } else if (item.resource_type === 'note') {
      setNoteModalOpen(item.resource_id);
    }
  };

  const onClickDelete = (resource_type, resource_id) => {
    if (resource_type === 'note') {
      deleteNote(resource_id, taskId);
    } else if (resource_type === 'repair') {
      deleteRepair(resource_id, taskId);
    }
  };

  const isItemClickable = item => (
    (item.resource_type === 'repair' || item.resource_type === 'note') &&
    item.op !== 'DELETE' && !item.deleted
  );

  const getItemField = (item, fieldName = 'description') => {
    let fieldVal = null;
    if (item.resource_type === 'repair') {
      const repair = repairs.find(r => r.id === item.resource_id);
      if (repair) {
        fieldVal = repair[fieldName];
      }
    } else if (item.resource_type === 'note') {
      const note = notes.find(n => n.id === item.resource_id);
      const field = fieldName === 'description' ? 'note_text' : fieldName;
      if (note) {
        fieldVal = note[field];
      }
    }
    return fieldVal;
  };

  const getIcon = (resource_type) => {
    if (resource_type === 'attachment') return <Paperclip />;
    return activityFormat[resource_type] && activityFormat[resource_type].icon();
  };

  const diffToBeShown = item => (
    item.resource_type === 'task' &&
    (item.column === 'description' || item.column === 'recommended_action')
  );

  return (
    <div style={{ marginTop: '1rem' }}>
      <LabelContainer>
        <Label>{t('taskHistory.title', { defaultValue: 'Task Activity' })}</Label>
      </LabelContainer>
      {loading && (
        <NoDataContainer><LoadingSvg /></NoDataContainer>
      )}
      {!loading && (
        _.isEmpty(items) ? (
          <NoDataContainer>{t('taskHistory.noActivities', { defaultValue: 'No activities to show' })}</NoDataContainer>
        ) : (
          items.map((item, idx) => (
            <TaskItemContainer
              key={idx}
              clickable={isItemClickable(item)}
            >
              <IconContainer paddingRight="1rem">
                {getIcon(item.resource_type)}
              </IconContainer>
              <FlexContainer
                direction="column"
                flexGrow={1}
                onClick={() => { if (isItemClickable(item)) onItemClickHandler(item); }}
              >
                <Text color={item.deleted ? colors.greyD : colors.black}>
                  {formHistorySentence(item, getItemField, t, currency)}
                  {diffToBeShown(item) && (
                    <DiffViewer marginLeft="5px" oldValue={item.old_value} newValue={item.new_value} />
                  )}
                </Text>
                <MetadataContainer fontSize="12px">
                  <SecondaryText>{formatDate(item.timestamp)}</SecondaryText>
                  {item.deleted && (
                    <SecondaryText color={colors.redL}>
                      {t('taskHistory.deleted', { defaultValue: '{{resource}} Deleted', resource: humanize(item.resource_type) })}
                    </SecondaryText>
                  )}
                </MetadataContainer>
                {getItemField(item, item.column) && (
                  <SecondaryText color={colors.black} marginTop="6px">
                    {getItemField(item, item.column)}
                  </SecondaryText>
                )}
                {item.resource_type === 'attachment' && (
                  <Attachments
                    originType="task"
                    originId={taskId}
                    attachments={item.attachments}
                    showExistingAtts
                    showBrowseButton={false}
                    allowUpload={false}
                    disableCarousel
                    showSelectedFiles
                  />
                )}
              </FlexContainer>
              {isItemClickable(item) && item.op === 'ADD' && (
                <IconContainer>
                  <span
                    onClick={() => onClickDelete(item.resource_type, item.resource_id)}
                    title={`Delete ${humanize(item.resource_type)}`}
                  >
                    <DeleteIconSvg />
                  </span>
                </IconContainer>
              )}
            </TaskItemContainer>
          ))
        )
      )}
    </div>
  );
}

const mapDispatchToProps = dispatch => ({
  deleteNote: bindActionCreators(deleteNote, dispatch),
  deleteRepair: bindActionCreators(deleteRepair, dispatch)
});

TaskHistory.propTypes = {
  items: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  taskId: PropTypes.number.isRequired,
  setRepairModalOpen: PropTypes.func,
  setNoteModalOpen: PropTypes.func,
  notes: PropTypes.array,
  repairs: PropTypes.array,
  currency: PropTypes.string
};

TaskHistory.defaultProps = {
  setRepairModalOpen: () => {},
  setNoteModalOpen: () => {},
  notes: [],
  repairs: [],
  currency: 'USD'
};

export default connect(null, mapDispatchToProps)(TaskHistory);
